import { useCallback, useMemo, useRef, useState, type ReactNode } from 'react';
import { useMixerModel } from '../../audio/useMixerModel';
import { AuthorText } from '../../components/AuthorText';
import { Credits } from '../../components/Credits';
import { Steve } from '../../components/Steve';
import { VuMeter } from '../../components/VuMeter';
import { S, faq, t, type Leaf } from '../../i18n/strings';
import { cx } from '../../lib/cx';
import { useActions, useSteve } from '../../state/context';
import { useAudioText, useGuestbookLines, useLogLines } from '../../state/derived';
import { XpBalloon } from './XpBalloon';
import { XpDialog } from './XpDialog';
import { XpMenuBar, type XpMenu } from './XpMenuBar';
import { XpScreensaver } from './XpScreensaver';
import { XpStartMenu, type StartEntry } from './XpStartMenu';
import styles from './xp.module.css';

/** Une boîte de dialogue, désignée par ses deux chaînes. */
interface DialogContent {
  readonly title: Leaf;
  readonly body: Leaf;
}

/** État de la fenêtre, comme dans le vrai gestionnaire de fenêtres. */
type WindowState = 'normal' | 'minimized' | 'maximized';

/**
 * Skin 7 — Windows XP : le site comme une fenêtre de l'Explorateur.
 *
 * Le parti pris : **Steve est traité comme un fichier système**. Il a des
 * propriétés, une date de modification, une taille (« 2 jambes, 2 bras ») et on
 * ne peut pas le désinstaller. La chrome est sérieuse et fidèle ; c'est le
 * contenu qui est absurde.
 *
 * Conséquence pour l'intégration : **rien n'est décoratif**. Le menu démarrer
 * s'ouvre, les six menus de l'Explorateur ont des entrées qui agissent, les
 * trois boutons de la barre de titre réduisent, agrandissent et refusent de
 * fermer, le bouton de la barre des tâches restaure la fenêtre, les quatre
 * tâches du volet emmènent quelque part. Une chrome d'époque qui ne répond pas
 * au clic se voit immédiatement.
 */
export function XpSkin(): ReactNode {
  const { lang, seen, draft, clock } = useSteve();
  const actions = useActions();
  const mixer = useMixerModel();
  const audio = useAudioText();
  const guestbook = useGuestbookLines();
  const questions = faq(lang);

  const [openMenu, setOpenMenu] = useState<string | null>(null);
  const [startOpen, setStartOpen] = useState(false);
  const [dialog, setDialog] = useState<DialogContent | null>(null);
  const [windowState, setWindowState] = useState<WindowState>('normal');
  /** Incrémenté par « Affichage → Écran de veille » : le jeton d'ouverture. */
  const [saverToken, setSaverToken] = useState(0);
  /** Incrémenté par le menu Steve : chaque action mérite sa bulle. */
  const [alertToken, setAlertToken] = useState(0);

  const log = useLogLines();

  /**
   * Déclenche une action du bac, puis demande la bulle qui l'annonce.
   *
   * Les deux mises à jour sont groupées par React : au rendu suivant, la
   * première ligne du journal est bien celle que l'action vient d'écrire.
   */
  const announce = (run: () => void): void => {
    run();
    setAlertToken((token) => token + 1);
  };

  const steveRef = useRef<HTMLDivElement>(null);
  const playerRef = useRef<HTMLFieldSetElement>(null);
  const helpRef = useRef<HTMLFieldSetElement>(null);
  const guestbookRef = useRef<HTMLFieldSetElement>(null);
  const draftRef = useRef<HTMLInputElement>(null);

  /** Ouvre une boîte de dialogue, en refermant ce qui traîne. */
  const say = useCallback((content: DialogContent): void => {
    setOpenMenu(null);
    setStartOpen(false);
    setDialog(content);
  }, []);

  /**
   * Va voir quelque chose dans la page. Restaure d'abord la fenêtre : une
   * commande qui ne ferait rien parce que la fenêtre est réduite serait un bug,
   * pas une blague.
   */
  const goTo = useCallback((target: React.RefObject<HTMLElement | null>): void => {
    setOpenMenu(null);
    setStartOpen(false);
    setWindowState((state) => (state === 'minimized' ? 'normal' : state));
    // Après le rendu qui rouvre la fenêtre, sinon la cible n'a pas de position.
    window.requestAnimationFrame(() => {
      target.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  }, []);

  const signGuestbook = useCallback((): void => {
    goTo(guestbookRef);
    window.requestAnimationFrame(() => {
      draftRef.current?.focus();
    });
  }, [goTo]);

  // ── Les six menus de l'Explorateur ──────────────────────────────────────
  const menus = useMemo<readonly XpMenu[]>(
    () => [
      {
        id: 'file',
        label: S.xp.chrome.menu_file,
        items: [
          { id: 'open', label: S.xp.menus.file_open, onSelect: () => { goTo(steveRef); } },
          {
            id: 'properties',
            label: S.xp.menus.file_properties,
            onSelect: () => {
              say({ title: S.xp.dialogs.properties_title, body: S.xp.dialogs.properties_body });
            },
          },
          {
            id: 'uninstall',
            label: S.xp.menus.file_uninstall,
            onSelect: () => {
              say({ title: S.xp.dialogs.uninstall_title, body: S.xp.dialogs.uninstall_body });
            },
          },
          {
            id: 'close',
            label: S.xp.menus.file_close,
            onSelect: () => {
              say({ title: S.xp.dialogs.close_title, body: S.xp.dialogs.close_body });
            },
          },
        ],
      },
      {
        id: 'edit',
        label: S.xp.chrome.menu_edit,
        items: [
          { id: 'select', label: S.xp.menus.edit_select, onSelect: () => { goTo(steveRef); } },
          {
            id: 'copy',
            label: S.xp.menus.edit_copy,
            onSelect: () => {
              say({ title: S.xp.dialogs.copy_title, body: S.xp.dialogs.copy_body });
            },
          },
          {
            id: 'paste',
            label: S.xp.menus.edit_paste,
            onSelect: () => {
              say({ title: S.xp.dialogs.paste_title, body: S.xp.dialogs.paste_body });
            },
          },
        ],
      },
      {
        id: 'view',
        label: S.xp.chrome.menu_view,
        items: [
          // « Actualiser » incrémente le compteur : c'est ce que fait un F5 sur
          // ce site, sans perdre la page.
          { id: 'refresh', label: S.xp.menus.view_refresh, onSelect: actions.bump },
          { id: 'thumbnails', label: S.xp.menus.view_thumbnails, disabled: true, onSelect: () => undefined },
          { id: 'details', label: S.xp.menus.view_details, checked: true, onSelect: () => { goTo(steveRef); } },
          {
            // L'écran de veille arrive seul après 40 s d'immobilité — autant
            // dire jamais quand on visite vraiment le site. Cette entrée est la
            // seule façon de le voir à la demande.
            id: 'screensaver',
            label: S.xp.menus.view_screensaver,
            onSelect: () => {
              setSaverToken((token) => token + 1);
            },
          },
        ],
      },
      {
        id: 'favorites',
        label: S.xp.chrome.menu_favorites,
        items: [
          { id: 'song', label: S.xp.menus.fav_song, onSelect: () => { goTo(playerRef); } },
          { id: 'guestbook', label: S.xp.menus.fav_guestbook, onSelect: signGuestbook },
          { id: 'pictures', label: S.xp.menus.fav_pictures, disabled: true, onSelect: () => undefined },
        ],
      },
      {
        id: 'steve',
        label: S.xp.chrome.menu_steve,
        items: [
          { id: 'walk', label: S.xp.menus.steve_walk, onSelect: () => { goTo(steveRef); } },
          /*
           * Ces trois actions vivent dans l'état partagé et se voient dans le
           * bac d'AquariumOS. Ici il n'y a pas de bac : sans accusé de
           * réception, elles ne produisaient rien du tout. La bulle de
           * notification lit la ligne que le journal vient d'écrire.
           */
          { id: 'feed', label: S.xp.menus.steve_feed, onSelect: () => { announce(actions.feed); } },
          {
            id: 'praise',
            label: S.xp.menus.steve_praise,
            onSelect: () => { announce(actions.praise); },
          },
          {
            id: 'bubbles',
            label: S.xp.menus.steve_bubbles,
            onSelect: () => { announce(actions.bubbles); },
          },
          {
            id: 'skin',
            label: S.xp.menus.steve_skin,
            onSelect: () => {
              actions.cycleSkin(0);
            },
          },
        ],
      },
      {
        id: 'help',
        label: S.xp.chrome.menu_help,
        items: [
          {
            id: 'about',
            label: S.xp.menus.help_about,
            onSelect: () => {
              say({ title: S.xp.dialogs.about_title, body: S.xp.dialogs.about_body });
            },
          },
          { id: 'support', label: S.xp.menus.help_support, onSelect: () => { goTo(helpRef); } },
        ],
      },
    ],
    [actions, goTo, say, signGuestbook],
  );

  // ── Menu démarrer ───────────────────────────────────────────────────────
  const programs = useMemo<readonly StartEntry[]>(
    () => [
      { id: 'explorer', label: S.xp.start.explorer, onSelect: () => { goTo(steveRef); } },
      { id: 'player', label: S.xp.start.player, onSelect: () => { goTo(playerRef); } },
      {
        id: 'search',
        label: S.xp.start.search,
        onSelect: () => {
          say({ title: S.xp.dialogs.search_title, body: S.xp.dialogs.search_body });
        },
      },
      {
        id: 'run',
        label: S.xp.start.run,
        onSelect: () => {
          say({ title: S.xp.dialogs.run_title, body: S.xp.dialogs.run_body });
        },
      },
    ],
    [goTo, say],
  );

  const places = useMemo<readonly StartEntry[]>(
    () => [
      { id: 'guestbook', label: S.xp.start.guestbook, onSelect: signGuestbook },
      { id: 'help', label: S.xp.start.help, onSelect: () => { goTo(helpRef); } },
      { id: 'all', label: S.xp.start.all_programs, onSelect: () => { goTo(playerRef); } },
    ],
    [goTo, signGuestbook],
  );

  const footer = useMemo<readonly StartEntry[]>(
    () => [
      {
        id: 'logoff',
        label: S.xp.start.logoff,
        onSelect: () => {
          actions.cycleSkin(0);
        },
      },
      {
        id: 'shutdown',
        label: S.xp.start.shutdown,
        onSelect: () => {
          say({ title: S.xp.dialogs.shutdown_title, body: S.xp.dialogs.shutdown_body });
        },
      },
    ],
    [actions, say],
  );

  const paneLinks = [
    { id: 'walk', label: S.xp.taskpane.manage_1, onSelect: () => { goTo(steveRef); } },
    { id: 'song', label: S.xp.taskpane.manage_2, onSelect: actions.toggleAudio },
    { id: 'sign', label: S.xp.taskpane.manage_3, onSelect: signGuestbook },
    {
      id: 'unplug',
      label: S.xp.taskpane.manage_4,
      onSelect: () => {
        say({ title: S.xp.dialogs.unplug_title, body: S.xp.dialogs.unplug_body });
      },
    },
  ];

  const minimized = windowState === 'minimized';
  const maximized = windowState === 'maximized';

  return (
    <div className={cx(styles.root, maximized && styles.rootMaximized)}>
      <div
        className={cx(
          styles.window,
          minimized && styles.minimized,
          maximized && styles.windowMaximized,
        )}
      >
        <div className={styles.titleBar}>
          <span className={styles.titleText}>{t(S.xp.chrome.window_title, lang)}</span>
          <div className={styles.titleButtons}>
            <button
              type="button"
              className={styles.titleButton}
              aria-label={t(S.xp.chrome.minimize, lang)}
              onClick={() => {
                setOpenMenu(null);
                setWindowState('minimized');
              }}
            >
              _
            </button>
            <button
              type="button"
              className={styles.titleButton}
              aria-label={t(
                windowState === 'maximized' ? S.xp.chrome.restore : S.xp.chrome.maximize,
                lang,
              )}
              aria-pressed={windowState === 'maximized'}
              onClick={() => {
                setWindowState((state) => (state === 'maximized' ? 'normal' : 'maximized'));
              }}
            >
              □
            </button>
            {/* La croix ne ferme rien : Steve est un dossier système. */}
            <button
              type="button"
              className={cx(styles.titleButton, styles.titleClose)}
              aria-label={t(S.xp.chrome.close, lang)}
              onClick={() => {
                say({ title: S.xp.dialogs.close_title, body: S.xp.dialogs.close_body });
              }}
            >
              ×
            </button>
          </div>
        </div>

        <XpMenuBar menus={menus} open={openMenu} lang={lang} onOpen={setOpenMenu} />

        <div className={styles.body}>
          <div className={styles.taskPane}>
            <div className={styles.panel}>
              <h2 className={styles.panelHead}>{t(S.xp.taskpane.manage_title, lang)}</h2>
              <div className={cx(styles.panelBody, styles.paneLinks)}>
                {paneLinks.map((link) => (
                  <button
                    key={link.id}
                    type="button"
                    className={styles.paneLink}
                    onClick={link.onSelect}
                  >
                    <span className={styles.paneArrow} aria-hidden="true">
                      ▶
                    </span>
                    <span>{t(link.label, lang)}</span>
                  </button>
                ))}
              </div>
            </div>

            <div className={styles.panel}>
              <h2 className={styles.panelHead}>{t(S.xp.taskpane.details_title, lang)}</h2>
              <div className={styles.panelBody}>
                <span className={styles.paneName}>Steve</span>
                <span>{t(S.xp.taskpane.details_kind, lang)}</span>
                <span>{t(S.xp.taskpane.details_modified, lang)}</span>
                <span>{t(S.xp.taskpane.details_size, lang)}</span>
                <span>{t(S.xp.taskpane.details_sessions, lang, { n: seen })}</span>
              </div>
            </div>
          </div>

          <div className={styles.content}>
            <section className={styles.hero} ref={steveRef}>
              <div className={styles.steveHolder}>
                <Steve bobDuration="3.2s" onClick={actions.bump} label={t(S.common.title, lang)} />
              </div>
              <div className={styles.heroText}>
                <h1 className={styles.title}>{t(S.xp.content.title, lang)}</h1>
                <p className={styles.subtitle}>{t(S.xp.content.subtitle, lang)}</p>
                <div className={styles.songRow}>
                  <button type="button" className={styles.button} onClick={actions.toggleAudio}>
                    {audio.label}
                  </button>
                  <span className={styles.hint} role="status">
                    {audio.hint}
                  </span>
                </div>
              </div>
            </section>

            <fieldset className={styles.group}>
              <legend className={styles.legend}>{t(S.xp.content.group_props, lang)}</legend>
              <p className={styles.paragraph}>{t(S.y2k.who_p1, lang)}</p>
              <p className={styles.paragraph}>
                <AuthorText linkClassName={styles.creditLink}>
                  {t(S.xp.content.p2, lang)}
                </AuthorText>
              </p>
            </fieldset>

            <div className={styles.stats}>
              <div className={styles.stat}>
                <span className={styles.statValue}>{seen}</span>
                <span className={styles.statLabel}>{t(S.xp.content.stat_seen, lang)}</span>
              </div>
              <div className={styles.stat}>
                <span className={styles.statValue}>2</span>
                <span className={styles.statLabel}>{t(S.xp.content.stat_legs, lang)}</span>
              </div>
              <div className={styles.stat}>
                <span className={styles.statValue}>100 %</span>
                <span className={styles.statLabel}>{t(S.xp.content.stat_orange, lang)}</span>
              </div>
            </div>

            <fieldset className={styles.group} ref={playerRef}>
              <legend className={styles.legend}>{t(S.xp.content.group_player, lang)}</legend>
              <div className={styles.player}>
                <VuMeter className={styles.vu} barClassName={styles.vuBar} />

                <div className={styles.pads}>
                  {mixer.pads.map((pad) => (
                    <button key={pad.index} type="button" className={styles.pad} onClick={pad.onHit}>
                      <span>{pad.label}</span>
                      <span className={styles.padTime}>{pad.timeLabel}</span>
                    </button>
                  ))}
                </div>

                <div className={styles.buttons}>
                  <button type="button" className={styles.button} onClick={mixer.onToggleHold}>
                    {mixer.holdLabel}
                  </button>
                  <button type="button" className={styles.button} onClick={mixer.onStop}>
                    {t(S.xp.mixer.stop, lang)}
                  </button>
                </div>

                {mixer.sliders.map((slider) => (
                  <div key={slider.key} className={styles.slider}>
                    <label className={styles.sliderLabel} htmlFor={`xp-${slider.key}`}>
                      {slider.label}
                    </label>
                    <input
                      id={`xp-${slider.key}`}
                      type="range"
                      className={styles.sliderInput}
                      min={slider.min}
                      max={slider.max}
                      step={slider.step}
                      value={slider.value}
                      onChange={(event) => {
                        slider.onChange(event.currentTarget.valueAsNumber);
                      }}
                    />
                    <output className={styles.sliderValue} htmlFor={`xp-${slider.key}`}>
                      {slider.valueLabel}
                    </output>
                  </div>
                ))}

                <div className={styles.buttons}>
                  {mixer.presets.map((preset) => (
                    <button
                      key={preset.name}
                      type="button"
                      className={styles.button}
                      onClick={preset.onSelect}
                    >
                      {preset.label}
                    </button>
                  ))}
                  <button type="button" className={styles.button} onClick={mixer.onToggleWobble}>
                    {mixer.wobbleLabel}
                  </button>
                </div>
              </div>
            </fieldset>

            {/* « Mes images » reste masqué tant que le client n'a pas fourni les
                visuels : quatre cadres vides ne valent pas mieux qu'aucun. */}
            <div className={styles.columns}>
              <fieldset className={styles.group} ref={helpRef}>
                <legend className={styles.legend}>{t(S.xp.content.group_help, lang)}</legend>
                <dl className={styles.faq}>
                  {questions.map((item) => (
                    <div key={item.question}>
                      <dt className={styles.question}>{item.question}</dt>
                      <dd className={styles.answer}>{item.answer}</dd>
                    </div>
                  ))}
                </dl>
              </fieldset>
            </div>

            <fieldset className={styles.group} ref={guestbookRef}>
              <legend className={styles.legend}>{t(S.xp.content.group_guestbook, lang)}</legend>
              <ul className={styles.entries}>
                {guestbook.map((entry) => (
                  <li key={entry.id} className={styles.entry}>
                    <span className={styles.entryWho}>{entry.who}</span>{' '}
                    <span className={styles.entryText}>— {entry.text}</span>
                  </li>
                ))}
              </ul>
              <div className={styles.compose}>
                <input
                  ref={draftRef}
                  type="text"
                  className={styles.input}
                  value={draft}
                  placeholder={t(S.guestbook.placeholder, lang)}
                  aria-label={t(S.guestbook.placeholder, lang)}
                  onChange={(event) => {
                    actions.setDraft(event.currentTarget.value);
                  }}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter') actions.postEntry();
                  }}
                />
                <button type="button" className={styles.button} onClick={actions.postEntry}>
                  {t(S.guestbook.publish, lang)}
                </button>
              </div>
            </fieldset>

            <footer className={styles.credits}>
              <Credits linkClassName={styles.creditLink} unofficial />
              <span>{t(S.common.egg_hint, lang)}</span>
            </footer>
          </div>
        </div>
      </div>

      <div className={styles.taskbar}>
        <button
          type="button"
          className={cx(styles.startButton, startOpen && styles.startOpen)}
          aria-haspopup="true"
          aria-expanded={startOpen}
          onClick={() => {
            setOpenMenu(null);
            setStartOpen((open) => !open);
          }}
        >
          <span className={styles.startLogo} aria-hidden="true" />
          {t(S.xp.chrome.start, lang)}
        </button>

        <button
          type="button"
          className={cx(
            styles.taskButton,
            !minimized && styles.taskButtonActive,
            // Sous 480 px le bouton est masqué pour gagner de la place, mais il
            // reste le seul moyen de faire revenir une fenêtre réduite.
            minimized && styles.taskButtonRestore,
          )}
          aria-pressed={!minimized}
          onClick={() => {
            setWindowState((state) => (state === 'minimized' ? 'normal' : 'minimized'));
          }}
        >
          {t(S.xp.chrome.taskbar_window, lang)}
        </button>

        {/* La zone de notification : l'état du son, l'horloge, et un clic qui
            emmène au lecteur — comme une icône de volume. */}
        <button
          type="button"
          className={styles.tray}
          aria-label={t(S.xp.chrome.notification_area, lang)}
          onClick={() => {
            goTo(playerRef);
          }}
        >
          <span role="status">{mixer.stateLabel}</span>
          <span>{clock}</span>
        </button>
      </div>

      {startOpen && (
        <XpStartMenu
          lang={lang}
          programs={programs}
          places={places}
          footer={footer}
          onClose={() => {
            setStartOpen(false);
          }}
        />
      )}

      {dialog && (
        <XpDialog
          title={dialog.title}
          body={dialog.body}
          lang={lang}
          onClose={() => {
            setDialog(null);
          }}
        />
      )}

      <XpBalloon alertToken={alertToken} alert={log[0]?.text ?? ''} />
      {/* Le déclenchement automatique tient compte de la lecture et des
          réglages du visiteur ; le jeton, lui, vient du menu Affichage. */}
      <XpScreensaver openToken={saverToken} />
    </div>
  );
}
